import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormAttachmentUploadRequirements } from '../attachments/form-attachment-requirements';
import { parseLocalDate } from '../../../utils/date-time-formatter';
import { GeneralFormData } from '../general-form-data.model';
import { FormStep } from '../form-steps.interface';

export class FallProtectionPlan {
  id?: string;
  submittedByName: string = '';
  submittedByEmail: string = '';
  submissionDate?: Date;
  date: Date | null = null;
  project: string = '';
  jobsiteAddress: string = '';
  // Fall Hazards - Individual boolean properties
  perimeterEdge: boolean = false;
  elevatorOpenings: boolean = false;
  stairwayOpenings: boolean = false;
  ventMechanicalOpenings: boolean = false;
  openSidedFloors: boolean = false;
  articulatedSnorkelLifts: boolean = false;
  unprotectedSkylights: boolean = false;
  otherFallHazard: boolean = false;
  otherFallHazardSpecify: string = '';
  // Fall Restraint/Arrest
  safetyHarnessLanyards: boolean = false;
  horizontalLines: boolean = false;
  guardrails: boolean = false;
  warningLines: boolean = false;
  otherFallRestraint: boolean = false;
  otherFallRestraintSpecify: string = '';
  // Articulated Lifts
  articulatedLiftsSafetyHarness: boolean = false;
  employeesTrained: string = '';
  documentationAvailable: string = '';
  alternateDocumentation: string = '';
  // Fall Protection System Proceedures
  assemblyDescription: string = '';
  maintenanceDescription: string = '';
  responsiblePersonName: string = '';
  // Material Handling
  cranes!: boolean;
  forklifts!: boolean;
  otherMaterialHandling!: boolean;
  otherMaterialHandlingSpecify: string = '';
  // Overhead Protection
  limitOverheadHazards!: boolean;
  materialSecured!: boolean;
  equipmentSecured!: boolean;
  barricading!: boolean;
  warningSignsPosted!: boolean;
  hardHatsRequired!: boolean;
  toeboardsInstalled!: boolean;
  otherOverheadProtection!: boolean;
  otherOverheadProtectionSpecify: string = '';
  // Emergency Procedures
  initiate911!: boolean;
  utilizeLiftTruck!: boolean;
  utilizeBoomLift!: boolean;
  erectLadders!: boolean;
  useDroplines!: boolean;
  assistEmergencyTeam!: boolean;
  suspensionTraumaStraps!: boolean;
  otherEmergencyProcedure!: boolean;
  otherEmergencyProcedureSpecify: string = '';
  additionalSafetyMeasures: string = '';
  employeeSignature: Array<string> = [];

  constructor(data?: Partial<FallProtectionPlan>) {
    // Set default date to today if not provided
    if (data) {
      Object.assign(this, {
        ...data,
        date: data.date ? parseLocalDate(data.date as any) : null,
        submissionDate: data.submissionDate
          ? new Date(data.submissionDate)
          : null,
      });
    }
  }

  static getFormInfo(): GeneralFormData {
    return {
      formType: 'fall-protection-plan',
      formTitle: 'Fall Protection Plan',
      formIcon: 'shield-outline',
      cardImage: 'assets/form-card-tiles/fall-protection-plan.png',
      formAttachmentRequirements: {
        signatures: [
          {
            identifier: 'employeeSignature',
            label: this.getFieldMappings()['employeeSignature'],
            required: true,
          },
        ],
      },
    };
  }

  static getFormSteps(): FormStep[] {
    return [
        {
            id: 'basic',
            shortTitle: 'Basic Info',
            title: 'Basic Information',
            icon: 'person-outline',
            description: 'Enter the basic information',
            sections: [
                {
                    fields: ['date', 'project', 'jobsiteAddress']
                }
            ]
        },
        {
            id: 'fall-hazards',
            shortTitle: 'Fall Hazards',
            title: 'Identify Fall Hazards',
            icon: 'warning-outline',
            description: 'Identify all fall hazards present',
            sections: [
                {
                    checkboxGroups: [
                        {
                            fields: [
                                'perimeterEdge',
                                'elevatorOpenings',
                                'stairwayOpenings',
                                'ventMechanicalOpenings',
                                'openSidedFloors',
                                'articulatedSnorkelLifts',
                                'unprotectedSkylights',
                                'otherFallHazard'
                            ],
                            checkboxLabel: this.getFieldMappings()['fallHazards']
                        }
                    ],
                    fields: ['otherFallHazardSpecify']
                }
            ]
        },
        {
            id: 'fall-protections',
            shortTitle: 'Fall Protections',
            title: 'Methods of Fall Protection',
            icon: 'shield-outline',
            description: 'Select fall protection methods and training information',
            sections: [
                {
                    checkboxGroups: [
                        {
                            fields: [
                                'safetyHarnessLanyards',
                                'horizontalLines',
                                'guardrails',
                                'warningLines',
                                'otherFallRestraint'
                            ],
                            checkboxLabel: this.getFieldMappings()['fallRestraint']
                        },
                        {
                            fields: [
                                'articulatedLiftsSafetyHarness'
                            ],
                            checkboxLabel: this.getFieldMappings()['articulatedLifts']
                        }
                    ],
                    fields: [
                        'otherFallRestraintSpecify',
                        'employeesTrained',
                        'documentationAvailable',
                        'alternateDocumentation'
                    ]
                }
            ]
        },
        {
            id: 'overhead-protection',
            shortTitle: 'Overhead Protection',
            title: 'Overhead Protection',
            icon: 'shield-checkmark-outline',
            description: 'Document overhead protection measures',
            sections: [
                {
                    checkboxGroups: [
                        {
                            fields: [
                                'cranes',
                                'forklifts',
                                'otherMaterialHandling'
                            ],
                            checkboxLabel: this.getFieldMappings()['materialHandling']
                        },
                        {
                            fields: [
                                'limitOverheadHazards',
                                'materialSecured',
                                'equipmentSecured',
                                'barricading',
                                'warningSignsPosted',
                                'hardHatsRequired',
                                'toeboardsInstalled',
                                'otherOverheadProtection'
                            ],
                            checkboxLabel: this.getFieldMappings()['overheadProtection']
                        }
                    ],
                    fields: [
                        'assemblyDescription',
                        'maintenanceDescription',
                        'responsiblePersonName',
                        'otherMaterialHandlingSpecify',
                        'otherOverheadProtectionSpecify'
                    ]
                }
            ]
        },
        {
            id: 'emergency-procedures',
            shortTitle: 'Emergency Procedures',
            title: 'Emergency Procedures',
            icon: 'medkit-outline',
            description: 'Document emergency procedures',
            sections: [
                {
                    checkboxGroups: [
                        {
                            fields: [
                                'initiate911',
                                'utilizeLiftTruck',
                                'utilizeBoomLift',
                                'erectLadders',
                                'useDroplines',
                                'assistEmergencyTeam',
                                'suspensionTraumaStraps',
                                'otherEmergencyProcedure'
                            ],
                            checkboxLabel: this.getFieldMappings()['emergencyProcedures']
                        }
                    ],
                    fields: [
                        'otherEmergencyProcedureSpecify',
                        'additionalSafetyMeasures',
                        'employeeSignature'
                    ]
                }
            ]
        }
    ];
}

  static getFieldMappings(): Record<string, string> {
    return {
      submittedByName: 'Submitter Name',
      submittedByEmail: 'Submitter Email',
      submissionDate: 'Submission Date',
      date: 'Date',
      project: 'Project',
      jobsiteAddress: 'Jobsite Address',
      // Fall Hazards
      fallHazards: 'Fall Hazards',
      perimeterEdge: 'Perimeter Edge',
      elevatorOpenings: 'Elevator Openings',
      stairwayOpenings: 'Stairway Openings',
      ventMechanicalOpenings: 'Vent, Mechanical Openings',
      openSidedFloors: 'Open-sided Floors',
      articulatedSnorkelLifts: 'Articulated Snorkel Lifts',
      unprotectedSkylights: 'Unprotected Skylights',
      otherFallHazard: 'Other (Specify)',
      otherFallHazardSpecify: 'If Other, Please Specify',
      // Methods of protection
      // Fall Restraint/Arrest
      fallRestraint: 'Fall Restraint/Arrest',
      safetyHarnessLanyards: 'Safety Harness/Lanyards',
      horizontalLines: 'Horizontal Lines',
      guardrails: 'Guardrails',
      warningLines: "Warning Lines(Minimun of 15' from Fall Hazard/Edge)",
      otherFallRestraint: 'Other (Specify)',
      otherFallRestraintSpecify: 'If Other Please Specify',
      // Articulated Lifts

      articulatedLifts: 'Articulated Lifts',
      articulatedLiftsSafetyHarness: 'Safety Harness',
      //Train employees
      employeesTrained: 'Employees Trained?',
      documentationAvailable: 'Documentation Available of Training Received?',
      alternateDocumentation:
        'If No Documentation Available, is there other Documentation that can be Located Elsewhere?',
      // Fall Protection System Proceedures
      assemblyDescription: 'Assembly',
      maintenanceDescription: 'Maintenance of Equipment or Systems Used',
      responsiblePersonName: 'Name of Reponsible Person(s)',
      // Material Handling
      
      materialHandling: 'Procedures for Handling, Storing, and Securing Tools and Materials at the Jobsite.-Describe how Materials will be Moved',
      cranes: 'Cranes',
      forklifts: 'Forklifts',
      otherMaterialHandling: 'Other',
      otherMaterialHandlingSpecify: 'Other Material Handling Procedures',
      // Overhead Protection
      overheadProtection: 'Overhead Protection Measures',
      limitOverheadHazards: 'Limit Overhead Hazards',
      materialSecured: 'Material Secured When Placed in Position',
      equipmentSecured: 'Equipment Secured When Not in Use',
      barricading: 'Barricading (Eliminating Access)',
      warningSignsPosted: 'Warning Signs Posted',
      hardHatsRequired: 'Hard Hats Required',
      toeboardsInstalled: 'Toeboards Installed Around Floor Openings',
      otherOverheadProtection: 'Other',
      otherOverheadProtectionSpecify: 'Other Overhead Protections',
      // Emergency Procedures
      emergencyProcedures: 'Emergency Procedures',
      initiate911: 'Initiate 911 Emergency Medical System',
      utilizeLiftTruck: 'Utilize Life Truck with Personal Platform',
      utilizeBoomLift: 'Utilize Articulated Boom Lift Platform',
      erectLadders: 'Erect Ladders',
      useDroplines: 'Use Droplines or Retraction Device',
      assistEmergencyTeam: 'Assist Medical, Fire, or Emergency Response Team',
      suspensionTraumaStraps: 'Use of Suspension Trauma Strapes',
      otherEmergencyProcedure: 'Other',
      otherEmergencyProcedureSpecify: 'Other Emergency Procedures',
      additionalSafetyMeasures:
        'List Other Safety Measures or Systems to be Used to Ensure and Establish an Adequate Fall',
      employeeSignature: 'Employee Signature',
    };
  }
}
